import { ChangeDetectionStrategy, Component, ElementRef, OnInit, effect, inject, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { arrowRotateCwIcon, loginIcon, paperPlaneIcon } from '@progress/kendo-svg-icons';
import DOMPurify from 'dompurify';
import { Marked } from 'marked';
import { CopilotChatService } from '../../services/custom-services/copilot-chat.service';

const markdown = new Marked({ breaks: true, gfm: true });

export function renderChatMarkdown(text: string): string {
  return DOMPurify.sanitize(markdown.parse(text, { async: false }), {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'del', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'h1', 'h2', 'h3', 'h4', 'hr'],
    ALLOWED_ATTR: ['href', 'title'],
    ALLOWED_URI_REGEXP: /^https?:\/\//i,
  });
}

@Component({
  selector: 'app-copilot-chat',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, KENDO_BUTTONS],
  templateUrl: './copilot-chat.component.html',
  styleUrl: './copilot-chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopilotChatComponent implements OnInit {
  readonly sendIcon = paperPlaneIcon;
  readonly retryIcon = arrowRotateCwIcon;
  readonly loginIcon = loginIcon;
  readonly chat = inject(CopilotChatService);
  private readonly builder = inject(FormBuilder);
  private readonly transcript = viewChild<ElementRef<HTMLElement>>('transcript');
  form!: FormGroup;
  readonly renderMarkdown = renderChatMarkdown;

  constructor() {
    effect(() => {
      const draft = this.chat.draft();
      if (this.form && this.form.controls['message'].value !== draft) {
        this.form.controls['message'].setValue(draft);
      }
    });
    effect(() => {
      this.chat.messages();
      const transcript = this.transcript()?.nativeElement;
      if (transcript) {
        const nearBottom = transcript.scrollHeight - transcript.scrollTop - transcript.clientHeight < 100;
        if (nearBottom) {
          requestAnimationFrame(() => (transcript.scrollTop = transcript.scrollHeight));
        }
      }
    });
  }

  ngOnInit(): void {
    this.initForm();
  }

  async send(suggestion?: string): Promise<void> {
    const text = suggestion ?? this.form.controls['message'].value;
    if (!text?.trim() || (!suggestion && this.form.invalid) || !this.chat.canSend()) {
      return;
    }
    await this.chat.send(text);
  }

  rememberDraft(): void {
    this.chat.draft.set(this.form.controls['message'].value);
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      void this.send();
    }
  }

  private initForm(): void {
    this.form = this.builder.group({
      message: [this.chat.draft(), [Validators.required, Validators.maxLength(8000)]],
    });
  }
}
